import { YamlMonacoEditor } from '@/components/projects/workflows'
import { ChainBuilder, ChainYamlSnapshotHistoryDialog, StartChainDialog } from '@/components/projects/chains'
import { parseChainYaml, serializeChainModel } from '@/lib/chainYaml'
import { extractValidationMessage } from '@/lib/validationErrors'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  useAuth,
  useChain,
  useCreateChain,
  useRoles,
  useUpdateChain,
  useValidateChainYaml,
} from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { CHAIN_DEFINITION_CATEGORIES, KEBAB_CASE_REGEX, LINKED_ENTITY_SCOPE_LABELS, LINKED_ENTITY_SCOPES, type ChainDefinitionCategory, type ChainModel, type LinkedEntityScope, type WorkflowValidationResult } from '@/types'
import type * as monaco from 'monaco-editor'
import { AlertTriangle, ArrowLeft, History, Play, Save, XCircle } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const DEFAULT_YAML = `inputs:
  - name: workItemId
    type: guid
    required: true

links:
  - name: First link
    workflow: technical-analysis
    triggerOn: success
    inputs:
      workItemId: \${{ chain.inputs.workItemId }}
`

export function ChainEditorPage() {
  const { projectId, chainId } = useParams()
  const navigate = useNavigate()
  const isNew = !chainId
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)

  const { data: existing, isLoading: loadingChain } = useChain(projectId!, chainId ?? '')
  const { data: roles } = useRoles()

  const createChain = useCreateChain(projectId!)
  const updateChain = useUpdateChain(projectId!)
  const validateChain = useValidateChainYaml(projectId!)

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)

  const [displayName, setDisplayName] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [version, setVersion] = useState('1.0.0')
  const [category, setCategory] = useState<ChainDefinitionCategory | ''>('')
  const [linkedEntityScope, setLinkedEntityScope] = useState<LinkedEntityScope | ''>('')
  const [roleIds, setRoleIds] = useState<string[]>([])
  const [isActive, setIsActive] = useState(true)
  const [yaml, setYaml] = useState(DEFAULT_YAML)
  const [validation, setValidation] = useState<WorkflowValidationResult | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [viewMode, setViewMode] = useState<'builder' | 'yaml'>('yaml')
  const [model, setModel] = useState<ChainModel>({ inputs: [], links: [] })
  const [historyOpen, setHistoryOpen] = useState(false)
  const [startOpen, setStartOpen] = useState(false)

  const roleOptions: MultiSelectOption[] = useMemo(
    () => (roles ?? []).map((r) => ({ value: r.id, label: r.name })),
    [roles],
  )

  useEffect(() => {
    if (isNew || !existing || initialized) return
    setDisplayName(existing.displayName)
    setName(existing.name)
    setDescription(existing.description ?? '')
    setVersion(existing.version)
    setCategory(existing.category ?? '')
    setLinkedEntityScope(existing.linkedEntityScope ?? '')
    setRoleIds(existing.permittedRoleIds)
    setIsActive(existing.isActive)
    setYaml(existing.yamlContent)
    try { setModel(parseChainYaml(existing.yamlContent)) } catch { /* keep empty model */ }
    setInitialized(true)
  }, [isNew, existing, initialized])

  useEffect(() => {
    if (!isNew) return
    try { setModel(parseChainYaml(DEFAULT_YAML)) } catch { /* keep empty model */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const switchTo = (mode: 'builder' | 'yaml') => {
    if (mode === viewMode) return
    if (mode === 'builder') {
      try {
        setModel(parseChainYaml(yaml))
        setViewMode('builder')
      } catch {
        toast.error('YAML is invalid — fix it before switching to the builder.')
      }
    } else {
      setViewMode('yaml')
    }
  }

  if (authLoading) return null
  const entryPermission = isNew ? Permissions.Project.Chain.Create : Permissions.Project.Chain.View
  if (!hasPermission(entryPermission, projectId)) {
    return <Navigate to={`/projects/${projectId}/chains`} replace />
  }
  const isSystemChain = !isNew && (existing?.isSystem ?? false)
  const canEdit = isNew || (hasPermission(Permissions.Project.Chain.Edit, projectId) && !isSystemChain)
  const readOnly = !canEdit
  const canStart = !isNew && !!existing?.isActive && hasPermission(Permissions.Project.Chain.Start, projectId)
  if (!isNew && loadingChain) {
    return (
      <div className="text-center py-12 text-muted-foreground" role="status" aria-live="polite" data-testid="chain-editor-loading">
        Loading chain…
      </div>
    )
  }

  const handleValidate = async () => {
    try {
      const result = await validateChain.mutateAsync(yaml)
      setValidation(result)
      if (result.isValid) toast.success('Chain YAML is valid')
    } catch {
      toast.error('Validation request failed')
    }
  }

  const validateForm = (): string | null => {
    if (!displayName.trim()) return 'Display name is required.'
    if (!name.trim() || !KEBAB_CASE_REGEX.test(name.trim())) return 'Name must be kebab-case (e.g. my-chain).'
    if (!version.trim()) return 'Version is required.'
    if (roleIds.length === 0) return 'At least one role must be assigned.'
    return null
  }

  const handleSave = async () => {
    const formError = validateForm()
    if (formError) {
      toast.error(formError)
      return
    }

    try {
      parseChainYaml(yaml)
    } catch {
      toast.error('YAML is invalid — fix it before saving.')
      return
    }

    const payload = {
      name: name.trim(),
      displayName: displayName.trim(),
      description: description.trim() || undefined,
      version: version.trim(),
      yamlContent: yaml,
      isActive,
      linkedEntityScope: linkedEntityScope || undefined,
      permittedRoleIds: roleIds,
      category: category || undefined,
    }

    try {
      if (isNew) {
        const r = await createChain.mutateAsync(payload)
        toast.success('Chain created')
        navigate(`/projects/${projectId}/chains/${(r as { chainDefinitionId: string }).chainDefinitionId}`)
      } else {
        const r = await updateChain.mutateAsync({ id: chainId!, data: payload })
        setVersion((r as { version?: string }).version || version)
        toast.success('Chain saved')
      }
      setValidation(null)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? 'Failed to save chain')
    }
  }

  const saving = createChain.isPending || updateChain.isPending
  const hasUnsavedYaml = !!existing && yaml !== existing.yamlContent

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-8" data-testid="chain-editor-page">
      <div className="shrink-0 px-8 pt-8 pb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/projects/${projectId}/chains`)}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Back to chains"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-foreground">{isNew ? 'New Chain' : displayName || 'Edit Chain'}</h1>
          {isSystemChain ? (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground" data-testid="chain-readonly-badge">System · read-only</span>
          ) : readOnly ? (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground" data-testid="chain-readonly-badge">Read-only</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {canStart && (
            <Button
              onClick={() => setStartOpen(true)}
              disabled={hasUnsavedYaml}
              title={hasUnsavedYaml ? 'Save your changes before starting — Start runs the saved definition.' : undefined}
              data-testid="chain-editor-start-button"
            >
              <Play className="h-4 w-4" />
              Start
            </Button>
          )}
          {!isNew && existing && (
            <Button variant="outline" onClick={() => setHistoryOpen(true)}>
              <History className="h-4 w-4" />
              Version History
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={handleValidate} disabled={validateChain.isPending}>
              {validateChain.isPending ? 'Validating…' : 'Validate'}
            </Button>
          )}
          {canEdit && (
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr] px-8 pb-8">
        <div className="bg-card rounded-2xl border border-border p-4 space-y-4 overflow-y-auto min-h-0">
          <div className="space-y-1.5">
            <Label htmlFor="chain-display-name">Display name</Label>
            <Input id="chain-display-name" data-testid="chain-display-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Feature Delivery" disabled={readOnly} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chain-name">Internal Name</Label>
            <Input id="chain-name" data-testid="chain-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="feature-delivery" disabled={readOnly} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chain-description">Description</Label>
            <Textarea id="chain-description" data-testid="chain-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} disabled={readOnly} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="chain-version">Version</Label>
              <Input id="chain-version" data-testid="chain-version-input" value={version} onChange={(e) => setVersion(e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="chain-category">Category</Label>
              <Select value={category || 'none'} onValueChange={(v) => setCategory(v === 'none' ? '' : (v as ChainDefinitionCategory))} disabled={readOnly}>
                <SelectTrigger id="chain-category" aria-label="Category" data-testid="chain-category-select">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {CHAIN_DEFINITION_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chain-linked-entity-scope">Linked To</Label>
            <Select value={linkedEntityScope || 'none'} onValueChange={(v) => setLinkedEntityScope(v === 'none' ? '' : (v as LinkedEntityScope))} disabled={readOnly}>
              <SelectTrigger id="chain-linked-entity-scope" aria-label="Linked To" data-testid="chain-linked-entity-scope-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {LINKED_ENTITY_SCOPES.map((scope) => (
                  <SelectItem key={scope} value={scope}>{LINKED_ENTITY_SCOPE_LABELS[scope]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chain-available-to">Available To</Label>
            <MultiSelect
              id="chain-available-to"
              options={roleOptions}
              value={roleIds}
              onChange={setRoleIds}
              placeholder="e.g. Developers, QAs, Project Owners"
              disabled={readOnly}
              displayMode="compact"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chain-status">Status</Label>
            <Select value={isActive ? 'true' : 'false'} onValueChange={(v) => setIsActive(v === 'true')} disabled={readOnly}>
              <SelectTrigger id="chain-status" aria-label="Status" data-testid="chain-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border flex flex-col min-h-0 overflow-hidden">
          <div className="shrink-0 p-4 pb-2">
            <div className="inline-flex rounded-lg border p-0.5 text-sm" data-testid="chain-view-toggle">
            <button
              type="button"
              className={cn('px-3 py-1.5 rounded-md font-bold', viewMode === 'builder' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}
              onClick={() => switchTo('builder')}
            >
              Builder
            </button>
            <button
              type="button"
              className={cn('px-3 py-1.5 rounded-md font-bold', viewMode === 'yaml' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}
              onClick={() => switchTo('yaml')}
            >
              YAML
            </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-2 space-y-3">
          {viewMode === 'builder' ? (
            <ChainBuilder projectId={projectId!} model={model} readOnly={readOnly} onChange={(m) => { setModel(m); setYaml(serializeChainModel(m)) }} />
          ) : (
            <div className="h-[480px] rounded-lg overflow-hidden border">
              <YamlMonacoEditor value={yaml} onChange={setYaml} editorRef={editorRef} readOnly={readOnly} />
            </div>
          )}

          {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div className="rounded-lg border p-3 text-sm space-y-2" data-testid="chain-validation-panel">
              {validation.errors.length > 0 && (
                <ul className="space-y-1">
                  {validation.errors.map((err, i) => (
                    <li key={i} className="flex items-start gap-2 text-danger">
                      <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        {err.lineNumber != null && <span className="font-mono">L{err.lineNumber} </span>}
                        [{err.layer}] {err.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {validation.warnings.length > 0 && (
                <ul className="space-y-1">
                  {validation.warnings.map((warn, i) => (
                    <li key={i} className="flex items-start gap-2 text-warning">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        {warn.lineNumber != null && <span className="font-mono">L{warn.lineNumber} </span>}
                        {warn.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {!isNew && existing && (
        <ChainYamlSnapshotHistoryDialog
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          projectId={projectId!}
          chainId={chainId!}
          currentYamlSnapshotId={existing.currentYamlSnapshotId}
          currentEditorYaml={yaml}
        />
      )}

      {!isNew && existing && (
        <StartChainDialog projectId={projectId!} chain={existing} open={startOpen} onOpenChange={setStartOpen} />
      )}
    </div>
  )
}
